import mongoose, { Types, Document, Model } from "mongoose";
import status from '../constant/status.json'
import { TFunction } from "i18next";
import { HttpException } from "utils/exceptions/httpException";


export const SkillEnum = {
    ELECTRICIAN: status.Skills.ELECTRICIAN,
    PLUMBER: status.Skills.PLUMBER,
    CARPENTER: status.Skills.CARPENTER,
    MASON: status.Skills.MASON,
    WELDER: status.Skills.WELDER,
    PAINTER: status.Skills.PAINTER,
    DRIVER: status.Skills.DRIVER,
    MACHINE_OPERATOR: status.Skills.MACHINE_OPERATOR,
    RIGGER: status.Skills.RIGGER,
    HELPER: status.Skills.HELPER,
} as const;


export const AvailableStatusEnum = {
    AVAILABLE: status.availability_status.Available,
    UNAVAILABLE: status.availability_status.Unavailable,
    ONLEAVE: status.availability_status.OnLeave,
    HOLIDAY: status.availability_status.Holiday
} as const;

export const GenderEnum = {
    MALE: status.Gender.MALE,
    FEMALE: status.Gender.FEMALE,
    OTHER: status.Gender.OTHER,
} as const;


export const OccupationEnum = {
    TRADE_ASSISTANT: status.Occupation.TRADE_ASSISTANT,
    SCAFFOLDER: status.Occupation.SCAFFOLDER,
    GENERAL_LABOR: status.Occupation.GENERAL_LABOR,
    SKILLED_LABOR: status.Occupation.SKILLED_LABOR,
    FOREMAN: status.Occupation.FOREMAN,
    SITE_SUPERVISOR: status.Occupation.SITE_SUPERVISOR,
    SAFETY_OFFICER: status.Occupation.SAFETY_OFFICER,
    PROJECT_MANAGER: status.Occupation.PROJECT_MANAGER,
    ADMIN_STAFF: status.Occupation.ADMIN_STAFF
}



export type AvailableStatus = typeof AvailableStatusEnum[keyof typeof AvailableStatusEnum];
export type Gender = typeof GenderEnum[keyof typeof GenderEnum];
// export type VerificationStatus = typeof VerificationStatusEnum[keyof typeof VerificationStatusEnum];
// export type MaritalStatus = typeof MaritalStatusEnum[keyof typeof MaritalStatusEnum];
export type Ticket = keyof typeof status.tickets;
export type Skill = typeof status.Skills[keyof typeof status.Skills];
export type ProfileCompletionStatus = typeof status.profileCompletionStatuses[keyof typeof status.profileCompletionStatuses];
export type OccupationType = keyof typeof status.Occupation



export interface Certificate {
    name: string;
    file: Types.ObjectId;
}

export interface Identification {
    type: string;
    file: Types.ObjectId;
    issuedAt?: Date;
    expiresAt?: Date;
}

export interface LabourModel extends Model<LabourDocument> {
    cleanupReferences(
        userId: string,
        refId: string,
        refType: 'Availability' | 'AllocationRequest',
        options?: { session?: mongoose.ClientSession }
    ): Promise<void>;
}

export interface LabourDocument extends Document {
    userId: Types.ObjectId;
    employee_id?: string;
    availability_status: AvailableStatus;
    skills?: Skill[];
    isDeleted: boolean;
    deletedAt?: Date;
    rejectionReason: string;
    profileCompletionStatus: ProfileCompletionStatus;
    profileCompletionPercentage: number;
    profileCompletedAt?: Date;
    certificates?: Certificate[];
    identification?: Identification[];
    experienceSummary?: string;
    occupation?: OccupationType;
    sitesInductedOn?: string[];
    tickets?: Ticket[];
    draft: boolean;
    isFirstUpdate: boolean
    availability: Types.ObjectId[];
    allocationRequest: Types.ObjectId[];
    step?: number

    deletedFiles?: string[]

    averageRating?: number
    ratingCount?: number
    createdAt?: Date
    updatedAt?: Date

}

export interface GetAllLabourParams {
    t: TFunction;
    page: number;
    limit: number;
    availability_status?: string;
    // verification_status?: string;
    search?: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    skills: string[]
    occupation?: string;
    tickets: string[]
    averageRating?: number
    ratingCount?: number
}

// Converts skill strings (case-insensitive) to enum values
export function mapSkillsCaseInsensitive(skills: string[]): Skill[] {
    const skillEnumMap = Object.entries(status.Skills).reduce((acc, [key, value]) => {
        acc[key.toLowerCase()] = value;
        return acc;
    }, {} as Record<string, Skill>);

    return skills.map(skill => {
        const value = skillEnumMap[skill.toLowerCase()];
        if (!value) throw new Error(`Invalid skill: ${skill}`);
        return value;
    });
}

export function mapTicketsCaseInsensitive(tickets: string[]): Ticket[] {
    const ticketMap = Object.entries(status.tickets).reduce((acc, [key, label]) => {
        acc[key.toLowerCase()] = key as Ticket;
        acc[label.toLowerCase()] = key as Ticket;
        return acc;
    }, {} as Record<string, Ticket>);

    return tickets.map(ticket => {
        const key = ticketMap[ticket.toLowerCase()];
        if (!key) throw new Error(`Invalid ticket: ${ticket}`);
        return key;
    });
}

export function mapSingleOccupationCaseInsensitive(occupation: string): OccupationType {
    const occupationMap = Object.entries(status.Occupation).reduce((acc, [key, label]) => {
        acc[key.toLowerCase()] = key as OccupationType;
        acc[label.toLowerCase()] = key as OccupationType;
        return acc;
    }, {} as Record<string, OccupationType>);

    const key = occupationMap[occupation.toLowerCase()];
    if (!key) throw new Error(`Invalid occupation: ${occupation}`);
    return key;
}



export function normalizeCertificateName(name: string): string {
    const skillMap = Object.values(status.Skills).reduce((acc, value) => {
        acc[value.toLowerCase()] = value;
        return acc;
    }, {} as Record<string, string>);
    const ticketMap = Object.values(status.tickets).reduce((acc, value) => {
        acc[value.toLowerCase()] = value;
        return acc;
    }, {} as Record<string, string>);

    const lowerName = name.toLowerCase();
    return skillMap[lowerName] || ticketMap[lowerName] || name;
}